import { put, take, fork, select, call, race } from 'redux-saga/effects'

import i18n from '../config/i18n'
import { handleRequest } from './requests'
import { createGetRequestPagination } from '../state'
import { STATE_KEYS } from '../state/requests'
import {
    BrandsActions,
    saveBrandList,
    saveBrand,
    CreateBrandRequestPayload,
    UpdateBrandRequestPayload,
    BrandListRequestPayload,
    BrandRequestPayload,
} from '../state/brands'
import {
    BrandsRequest,
    BrandsResponse,
    getBrand,
    getBrands,
    CreateBrandResponse,
    createBrand,
    updateBrand,
} from '../services/api/brands'
import { showSnackBar } from '../services/toast'
import { COUNTRY_ENUM } from 'services/api/constants'

export function* handleGetBrand(payload: BrandRequestPayload): any {
    yield handleRequest<BrandRequestPayload, CreateBrandResponse>({
        stateKey: STATE_KEYS.GetBrand,
        payload,
        ApiHandler: getBrand,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveBrand(response.data.data))
            }
        },
    })
}

export function* watchGetBrandRequestFlow(): any {
    while (true) {
        const action = yield take(BrandsActions.GET_BRAND)
        yield fork(handleGetBrand, action.payload)
    }
}

export function* handleGetBrandsList(payload: BrandListRequestPayload): any {
    const { keyword, country, clear } = payload
    const pagination = yield select(createGetRequestPagination(STATE_KEYS.BrandsList))
    const { hasMore, next } = pagination
    let countryFilter
    if (country && Object.values(COUNTRY_ENUM).includes(country as COUNTRY_ENUM)) {
        countryFilter = country
    }
    yield handleRequest<BrandsRequest, BrandsResponse>({
        stateKey: STATE_KEYS.BrandsList,
        payload: {
            ...(clear ? { limit: 100 } : hasMore ? { url: next } : { limit: 100 }),
            keyword,
            country: countryFilter,
        },
        ApiHandler: getBrands,
        *onSuccess(response) {
            yield put(
                saveBrandList({
                    data: response.data !== undefined ? response.data.data.results : undefined,
                    clear,
                }),
            )
            if (payload.onSuccessCallback) {
                yield payload.onSuccessCallback()
            }
        },
        *onError() {
            if (payload.onErrorCallback) {
                yield payload.onErrorCallback()
            }
        },
    })
}

export function* watchBrandsListRequestFlow(): any {
    while (true) {
        const action = yield take(BrandsActions.BRAND_LIST_REQUEST)
        yield race({
            task: fork(handleGetBrandsList, action.payload),
            cancel: take(BrandsActions.BRAND_LIST_CANCEL),
        })
    }
}

export function* handleCreateBrandRequest(payload: CreateBrandRequestPayload, onSuccess?: () => void): any {
    yield handleRequest<CreateBrandRequestPayload, CreateBrandResponse>({
        stateKey: STATE_KEYS.CreateBrand,
        payload,
        ApiHandler: createBrand,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveBrand(response.data.data))
                if (onSuccess) onSuccess()
            }
        },
    })
}

export function* watchCreateBrandRequestFlow(): any {
    while (true) {
        const action = yield take(BrandsActions.CREATE_BRAND_REQUEST)
        yield fork(handleCreateBrandRequest, action.payload, action.onSuccess)
    }
}

export function* handleUpdateBrandRequest(payload: UpdateBrandRequestPayload, onSuccess?: () => void): any {
    yield handleRequest<UpdateBrandRequestPayload, CreateBrandResponse>({
        stateKey: STATE_KEYS.UpdateBrand,
        payload,
        ApiHandler: updateBrand,
        *onSuccess(response) {
            if (response.data !== undefined) {
                yield put(saveBrand(response.data.data))
                yield call(showSnackBar, {
                    title: i18n.t('successMessage.brandUpdatedTitle', 'Brand updated!'),
                    type: 'positive',
                })
                if (onSuccess) onSuccess()
            }
        },
    })
}

export function* watchUpdateBrandRequestFlow(): any {
    while (true) {
        const action = yield take(BrandsActions.UPDATE_BRAND_REQUEST)
        yield fork(handleUpdateBrandRequest, action.payload, action.onSuccess)
    }
}
